// Cross-tab notification when appData.set persists a value. Listeners get
// the new value pushed via BroadcastChannel; tabs that didn't initiate
// the write react in real time. Falls back to a no-op when
// BroadcastChannel is unavailable (older Safari, server-side rendering).
//
// Wave 5 #29: Each HookProvider mount gets a unique `senderId` (lazy module
// singleton in the simple factory; passed explicitly when wired through
// HookProvider). Broadcasts include the senderId; subscribers ignore
// messages whose senderId matches their own. This avoids same-tab echo
// (BroadcastChannel only excludes the exact instance that posted, not
// other instances in the same tab) and keeps cross-tab semantics intact.

export type AppDataSubscriber = <T = unknown>(
  key: string,
  callback: (value: T) => void,
) => () => void;

interface BroadcastPayload {
  key: string;
  value: unknown;
  senderId?: string;
}

const channelNameFor = (appId: string) => `hook-sdk:appdata:${appId}`;

let _moduleSenderId: string | null = null;
function moduleSenderId(): string {
  if (_moduleSenderId !== null) return _moduleSenderId;
  // Best-effort UUID v4: use crypto.randomUUID when available, else random.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    _moduleSenderId = crypto.randomUUID();
  } else {
    _moduleSenderId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
  return _moduleSenderId;
}

export function createAppDataSubscribe(appId: string, ownSenderId?: string): AppDataSubscriber {
  const myId = ownSenderId ?? moduleSenderId();
  return <T,>(key: string, callback: (value: T) => void) => {
    if (typeof BroadcastChannel === 'undefined') return () => {};
    const channel = new BroadcastChannel(channelNameFor(appId));
    const handler = (event: MessageEvent) => {
      const data = event.data as BroadcastPayload | null | undefined;
      if (!data || data.key !== key) return;
      if (data.senderId && data.senderId === myId) return;
      callback(data.value as T);
    };
    channel.addEventListener('message', handler);
    return () => {
      channel.removeEventListener('message', handler);
      channel.close();
    };
  };
}

export function broadcastAppDataChange(
  appId: string,
  key: string,
  value: unknown,
  senderId?: string,
): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(channelNameFor(appId));
  channel.postMessage({ key, value, senderId: senderId ?? moduleSenderId() });
  channel.close();
}
